/* Pitch Kit · Driver view — mobile-first, no login (sandbox org only). Big one-tap status buttons that sync live to Admin + Customer views. */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Truck, MapPin, CheckCircle2, Navigation, Droplets, Flag, Loader2 } from "lucide-react";
import { API_BASE } from "@/lib/api";
import useLiveRefresh from "@/hooks/useLiveRefresh";

const STEPS = [
  { action: "depart", label: "Depart Plant", icon: Navigation, from: ["loading", "available", "slumping"], tint: "bg-orange-500" },
  { action: "arrived", label: "Arrived On Site", icon: MapPin, from: ["en_route"], tint: "bg-sky-500" },
  { action: "discharge", label: "Start Discharge", icon: Droplets, from: ["on_site"], tint: "bg-violet-500" },
  { action: "finish", label: "Finish · Docket Signed", icon: Flag, from: ["discharging", "on_site"], tint: "bg-emerald-500" },
];
const STATUS_LABEL = { loading: "LOADING AT PLANT", available: "AT PLANT", slumping: "SLUMP CHECK", en_route: "EN ROUTE", on_site: "ON SITE", discharging: "DISCHARGING", returning: "RETURNING TO PLANT" };

export default function DemoDriver() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [truckId, setTruckId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/public/demo-driver/${token}`);
      setData(r.data);
      setTruckId((cur) => cur || r.data?.active_truck?.id || null);
      setError(null);
    } catch (e) {
      setError(e.response?.status === 404 ? "This driver link is not valid" : "Cannot reach server");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, { intervalMs: 5000, org: data?.supplier?.org_id || "org_demo_sample" });

  const truck = data?.trucks?.find((t) => t.id === truckId) || data?.active_truck || data?.trucks?.[0];
  const status = truck?.status;

  const act = async (action) => {
    if (!truck) return;
    setBusy(action);
    try {
      const r = await axios.post(`${API_BASE}/public/demo-driver/${token}/action`, { truck_id: truck.id, action });
      setData(r.data);
      setTruckId(truck.id);
      if (navigator.vibrate) navigator.vibrate(30);
    } catch (e) {
      setError(e.response?.data?.detail || "Action failed");
      setTimeout(() => setError(null), 2500);
    } finally {
      setBusy(null);
    }
  };

  if (error && !data) return <div className="min-h-screen flex items-center justify-center bg-[rgb(var(--bg))] text-red-400 mono" data-testid="demo-driver-error">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[rgb(var(--bg))]"><Loader2 className="w-6 h-6 animate-spin text-primary-orange" /></div>;

  const job = data.job;
  const pct = Math.min(100, Math.round(((job.delivered_m3 || 0) / (job.quantity_m3 || 1)) * 100));
  const doneIdx = ["loading", "en_route", "on_site", "discharging", "returning"].indexOf(status);

  return (
    <div className="min-h-screen bg-[rgb(var(--bg))] text-ink flex flex-col max-w-md mx-auto" data-testid="demo-driver-page">
      <header className="px-4 pt-4 pb-3 border-b-2 border-primary-orange bg-surface sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.3em] text-muted-industrial">ArmorBatch Driver · {data.supplier?.name}</div>
            <div className="heading text-2xl font-black flex items-center gap-2" data-testid="demo-driver-truck">
              <Truck className="w-6 h-6 text-primary-orange" /> {truck?.number || "—"}
              <span className="text-sm font-normal text-muted-industrial">{truck?.driver_name}</span>
            </div>
          </div>
          <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-sky-500 text-black font-bold">● live sync</span>
        </div>
        {data.trucks?.length > 1 && (
          <div className="flex gap-1 mt-2 overflow-x-auto">
            {data.trucks.map((t) => (
              <button key={t.id} type="button" data-testid={`demo-driver-pick-${t.number}`} onClick={() => setTruckId(t.id)}
                className={`mono text-[10px] px-2 py-1 border shrink-0 ${t.id === truck?.id ? "border-primary-orange text-primary-orange" : "border-industrial text-muted-industrial"}`}>
                {t.number} · {STATUS_LABEL[t.status] || t.status}
              </button>
            ))}
          </div>
        )}
      </header>

      <main className="flex-1 px-4 py-4 space-y-4">
        <section className="border border-industrial bg-surface p-4" data-testid="demo-driver-job">
          <div className="label-tag">ACTIVE JOB · {job.time_required}</div>
          <div className="font-black text-lg leading-tight">{job.customer_name}</div>
          <div className="text-sm text-muted-industrial flex items-start gap-1 mt-0.5"><MapPin className="w-4 h-4 shrink-0 mt-0.5" />{job.site_name} · {job.site_address}</div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-center">
            <div className="border border-industrial py-2"><div className="mono text-lg font-black">{job.mix_code}</div><div className="label-tag">MIX</div></div>
            <div className="border border-industrial py-2"><div className="mono text-lg font-black">{job.slump_mm}</div><div className="label-tag">SLUMP mm</div></div>
            <div className="border border-industrial py-2"><div className="mono text-lg font-black">{truck?.capacity_m3 ?? "—"}</div><div className="label-tag">THIS LOAD m³</div></div>
          </div>
          <div className="mt-3">
            <div className="flex justify-between mono text-[10px] uppercase tracking-widest text-muted-industrial"><span>Job progress</span><span data-testid="demo-driver-progress">{job.delivered_m3} / {job.quantity_m3} m³</span></div>
            <div className="h-2 bg-surface-2 border border-industrial mt-1"><div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
          </div>
        </section>

        <section className="border border-industrial bg-surface p-4" data-testid="demo-driver-status">
          <div className="label-tag">TRUCK STATUS</div>
          <div className="heading text-3xl font-black text-primary-orange" data-testid="demo-driver-status-text">{STATUS_LABEL[status] || status || "—"}</div>
          {data.docket && <div className="mono text-[10px] text-muted-industrial mt-1">Docket {data.docket.docket_no} · {data.docket.status}</div>}
        </section>

        <div className="grid grid-cols-1 gap-3">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const enabled = s.from.includes(status);
            const done = doneIdx > i || status === "returning";
            return (
              <button key={s.action} type="button" data-testid={`demo-driver-${s.action}`} disabled={!enabled || !!busy} onClick={() => act(s.action)}
                className={`relative w-full h-20 flex items-center gap-4 px-5 text-left border-2 transition-all active:scale-[0.98]
                  ${enabled ? `${s.tint} border-transparent text-black shadow-lg` : done ? "border-emerald-600/60 text-emerald-500 bg-emerald-500/5" : "border-industrial text-muted-industrial bg-surface opacity-60"}`}>
                {done && !enabled ? <CheckCircle2 className="w-8 h-8" /> : <Icon className="w-8 h-8" />}
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.3em] opacity-70">Step {i + 1}</div>
                  <div className="heading text-xl font-black uppercase">{busy === s.action ? "Syncing…" : s.label}</div>
                </div>
              </button>
            );
          })}
        </div>
        {status === "returning" && (
          <div className="border border-emerald-600 bg-emerald-500/10 p-4 text-sm text-emerald-400" data-testid="demo-driver-complete">
            Load delivered — docket signed and synced to Dispatch, the customer tracker and Accounts. Truck is returning to plant; the next load is queued automatically.
          </div>
        )}
        {error && <div className="text-xs text-red-400 mono" data-testid="demo-driver-action-error">{error}</div>}
      </main>
      <footer className="px-4 py-3 text-center mono text-[9px] uppercase tracking-widest text-muted-industrial">ArmorBatch Global · Driver PWA · demo sandbox</footer>
    </div>
  );
}
